import clsx from 'clsx'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useContext, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  ModalPanel,
  ModalSidebarContext,
  TabSwitch,
  useReportStillProcessing,
} from '../../../../../components/ModalSidebar'
import { isTaskStillProcessing } from '../../../../../util/hub-task-management/taskStillProcessing'
import Groups from '../Groups'
import Licenses from '../Licenses'
import UserProperties from '../UserProperties'
import useHydrateAssignedLicenses from '../useHydrateAssignedLicenses'
import styles from './EditFormContent.module.scss'
import UnfinishedSaveAlert from './UnfinishedSaveAlert/UnfinishedSaveAlert'
import { SAVE_STEPS } from '../saveSteps'

function EditFormContent({
  modalSubmitStatusProp,
  submitErrorProp,
  unfinishedSaveProp = null,
  employeeEmailOptionsProp,
  modalTabsProp,
  employeeProp,
  intlProp,
  isIntegratedProp = false,
  isGwsProp = false,
  allEmployeesGroupsProp,
  customerLicensesProp,
  isFetchingCustomerLicensesProp,
  errorFetchingCustomerLicensesProp,
  syncCustomerLicensesProp,
  isSyncingCustomerLicensesProp,
  assignedLicenseIdsProp,
  currentEmployeeIdProp,
  onLicensesAppliedProp,
}) {
  const { submitForm } = useFormikContext()
  const { onHideWithConfirmation, headerBackButtonAction } = useContext(ModalSidebarContext)
  useHydrateAssignedLicenses(assignedLicenseIdsProp, currentEmployeeIdProp, onLicensesAppliedProp)

  const errorBackButton = useMemo(
    () => ({
      variant: 'secondary',
      label: 'GENERAL.BACK',
      onClick: headerBackButtonAction || onHideWithConfirmation,
    }),
    [headerBackButtonAction, onHideWithConfirmation],
  )

  const hasSubmitError = modalSubmitStatusProp === 'error'
  const isStillProcessing = hasSubmitError && isTaskStillProcessing(submitErrorProp)
  // ModalPanel then shows only Close, so the edit isn't saved again while it's still running
  useReportStillProcessing(isStillProcessing)

  const handleFormSubmit = (e) => {
    e.preventDefault()
    if (isStillProcessing) return
    submitForm()
  }

  return (
    <ModalPanel
      leftButton={hasSubmitError ? errorBackButton : 'cancel'}
      rightButton={{
        label: 'GENERAL.SAVE',
        variant: 'primary',
        onClick: () => submitForm(),
        disabled: modalSubmitStatusProp === 'loading',
        id: 'edit-employee-data-submit-btn',
      }}
      tabs={modalTabsProp}
    >
      <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
        {isStillProcessing && (
          <div className="section px-4 py-3">
            <UnfinishedSaveAlert
              processingStep={unfinishedSaveProp?.processingStep}
              notSentSteps={unfinishedSaveProp?.notSentSteps}
            />
          </div>
        )}
        {hasSubmitError && !isStillProcessing && (
          <div className={clsx('section px-4 py-3', styles.errorBanner)}>
            <div className="d-flex align-items-center">
              <i className={clsx('fas fa-circle-xmark me-3', styles.errorIcon)} />
              <div className="flex-grow-1">
                <h5 className="mb-1 text-color-danger">
                  <FormattedMessage id="GENERAL.ACTION_FAILED" />
                </h5>
                <p className={clsx('mb-0', styles.errorMessage)}>
                  {typeof submitErrorProp === 'string' && submitErrorProp.startsWith('GENERAL.') ? (
                    <FormattedMessage id={submitErrorProp} />
                  ) : (
                    submitErrorProp
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        <TabSwitch tab="userproperties">
          <UserProperties
            employee={employeeProp}
            intl={intlProp}
            employeeEmailOptions={employeeEmailOptionsProp}
            isIntegrated={isIntegratedProp}
          />
        </TabSwitch>

        {isIntegratedProp && !isGwsProp && (
          <TabSwitch tab="groups">
            <Groups
              employee={employeeProp}
              intl={intlProp}
              allEmployeesGroups={allEmployeesGroupsProp}
              isGws={isGwsProp}
            />
          </TabSwitch>
        )}

        {isIntegratedProp && (
          <TabSwitch tab="licenses">
            <Licenses
              employee={employeeProp}
              intl={intlProp}
              customerLicenses={customerLicensesProp}
              isFetchingCustomerLicenses={isFetchingCustomerLicensesProp}
              errorFetchingCustomerLicenses={errorFetchingCustomerLicensesProp}
              syncCustomerLicenses={syncCustomerLicensesProp}
              isSyncingCustomerLicenses={isSyncingCustomerLicensesProp}
              isGws={isGwsProp}
            />
          </TabSwitch>
        )}
      </form>
    </ModalPanel>
  )
}

EditFormContent.propTypes = {
  modalSubmitStatusProp: PropTypes.string,
  submitErrorProp: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  unfinishedSaveProp: PropTypes.shape({
    processingStep: PropTypes.oneOf(Object.values(SAVE_STEPS)),
    notSentSteps: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SAVE_STEPS))),
  }),
  employeeEmailOptionsProp: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  modalTabsProp: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node,
    }),
  ),
  employeeProp: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  intlProp: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isIntegratedProp: PropTypes.bool,
  isGwsProp: PropTypes.bool,
  allEmployeesGroupsProp: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  customerLicensesProp: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  isFetchingCustomerLicensesProp: PropTypes.bool,
  errorFetchingCustomerLicensesProp: PropTypes.bool,
  syncCustomerLicensesProp: PropTypes.func,
  isSyncingCustomerLicensesProp: PropTypes.bool,
  assignedLicenseIdsProp: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  currentEmployeeIdProp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLicensesAppliedProp: PropTypes.func,
}

export default React.memo(EditFormContent)
